import React, { createContext, useState, useEffect } from "react";
import { useUser } from "./UserContext"; // Ensure this provides current user info
import { fetchCart, addToCartApi, removeFromCartApi, clearCartApi, setCartDiscountApi } from "./api/api";

export const CartContext = createContext();

const withComputedDiscount = (item) => {
    const quantity = parseFloat(item.quantity) || 1;
    const price = parseFloat(item.price) || 0;
    const conversionFactor = parseFloat(item.conversion_factor) || 1;
    const threshold = parseFloat(item.quantity_discount_threshold) || 0;
    const amount = parseFloat(item.quantity_discount_amount) || 0;
    const active = !!item.quantity_discount_is_active;
    const lineSubtotal = quantity * price;
    const quantityInBaseUnits = quantity * conversionFactor;
    const quantityDiscount = active && threshold > 0 && amount > 0 && quantityInBaseUnits >= threshold
        ? Math.floor(quantityInBaseUnits / threshold) * amount
        : 0;

    return {
        ...item,
        quantity,
        price,
        conversion_factor: conversionFactor,
        quantity_discount_threshold: threshold,
        quantity_discount_amount: amount,
        quantity_discount_is_active: active,
        quantity_discount: quantityDiscount,
        quantity_discount_reason: quantityDiscount > 0 ? `Quantity discount: -Ksh. ${quantityDiscount.toFixed(2)}` : '',
        line_subtotal: lineSubtotal,
        discounted_subtotal: Math.max(0, lineSubtotal - quantityDiscount),
    };
};

const normalizeCartItem = (item) => withComputedDiscount({
    id: item.id,
    product: item.product,
    name: item.name,
    unit_name: item.unit_name,
    quantity: parseFloat(item.quantity) || 1,
    price: parseFloat(item.price) || 0.00,
    is_vat_applicable: item.is_vat_applicable,
    isAlternativeUnit: !!item.unit_id,
    unit_id: item.unit_id || null,
    conversion_factor: parseFloat(item.conversion_factor) || 1.0,
    unit_cost_price: parseFloat(item.unit_cost_price) || 0.00,
    quantity_discount_threshold: parseFloat(item.quantity_discount_threshold) || 0,
    quantity_discount_amount: parseFloat(item.quantity_discount_amount) || 0,
    quantity_discount_is_active: !!item.quantity_discount_is_active,
    quantity_discount: parseFloat(item.quantity_discount) || 0,
    quantity_discount_reason: item.quantity_discount_reason || '',
    line_subtotal: parseFloat(item.line_subtotal) || 0,
    discounted_subtotal: parseFloat(item.discounted_subtotal) || 0,
});

export const CartProvider = ({ children }) => {
    const { user } = useUser(); // Get current user
    const [cart, setCart] = useState([]);
    const [discount, setDiscount] = useState(0);

    // persist discount (Global, browser-level)
    // useEffect(() => {
    //     localStorage.setItem("cart_discount", discount);
    // }, [discount]);

    // Load cart from backend whenever the user changes
    useEffect(() => {
        if (user) {
            const loadCart = async () => {
                try {
                    const cartData = await fetchCart(); // Fetch cart from backend
                    setDiscount(Number(cartData.discount) || 0);
                    // console.log('Cart loaded:', cartData);
                    if (cartData && Array.isArray(cartData.items)) {
                        // Transform items if necessary
                        const transformedItems = cartData.items.map(normalizeCartItem);
                        setCart(transformedItems);
                    } else {
                        setCart([]);
                    }
                } catch (error) {
                    console.error('Failed to load cart:', error);
                    setCart([]);
                }
            };
            loadCart();
        } else {
            setCart([]); // Clear cart if no user is logged in
        }
    }, [user]);

    // Function to add product to cart via backend API
    const addToCart = async (product) => {
        try {
            const response = await addToCartApi(product); // Add to cart via API
            // console.log('Added to cart:', response);

            // Update local cart state with the new item
            setCart(prevCart => {
                // Check if item already exists in cart
                const existingIndex = prevCart.findIndex(item => item.id === response.id);

                console.log('server returned:', response);
                console.log('cart:', prevCart);

                if (existingIndex !== -1) {
                    // Update the existing item (e.g., quantity, price)
                    const updatedCart = [...prevCart];
                    updatedCart[existingIndex] = normalizeCartItem(response);
                    console.log('updated cart:', updatedCart);
                    return updatedCart;
                }

                // Add the new item if it doesn't already exist
                return [...prevCart, normalizeCartItem(response)];
            });
        } catch (error) {
            console.error('Failed to add to cart:', error);
        }
    };


    // Function to remove item from cart via backend API
    const removeFromCart = async (item_id) => {
        try {
            await removeFromCartApi(item_id); // Remove item via API
            setCart(prevCart => prevCart.filter(item => item.id !== item_id));
        } catch (error) {
            console.error('Failed to remove from cart:', error);
        }
    };

    // Function to clear the cart via backend API
    const clearCart = async () => {
        try {
            await clearCartApi(); // Clear cart via API
            setCart([]);
            // Also clear any persisted cart data in localStorage or sessionStorage
            setDiscount(0);
            localStorage.removeItem('cart_discount');
        } catch (error) {
            console.error('Failed to clear cart:', error);
        }
    };

    // Function to update cart items (e.g., quantity)
    const updateCart = async (index, updatedItem) => {
        try {
            // Optionally, send an API request to update the item
            // For now, just update the local state
            const updatedCart = [...cart];
            updatedCart[index] = withComputedDiscount(updatedItem);
            setCart(updatedCart);
            // If needed, send an API request to update the item
        } catch (error) {
            console.error('Failed to update cart:', error);
        }
    };

    // added
    const updateDiscount = async (value) => {
        const clean = Math.max(0, Number(value) || 0);
        setDiscount(clean);
        await setCartDiscountApi(clean);
    }
    // end of added

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, updateCart, discount, setDiscount: updateDiscount }}>
            {children}
        </CartContext.Provider>
    );
};
